import React, { useState, useEffect } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { pmt2Service, LatestGpoResultsData } from '@/services/pmt2Service';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { LinkIcon, CheckCircle, AlertCircle } from 'lucide-react'; // Import icons
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { cn } from "@/lib/utils"; // Import cn utility
import { usePMT3Selection } from '@/context/PMT3SelectionContext';

interface LatestGpoResultsDashboardProps {
  product: PMT2Product | null;
}

// Reusable fetchClientUrl function (consider moving to shared util)
const fetchClientUrl = async (companyName: string, docId: string): Promise<string | null> => {
    if (!companyName || !docId) return null;
    try {
        const sanitizedDocId = docId.replace(/\//g, '_').replace(/\./g, '_');
        const urlDocRef = doc(db, 'pmt2_clienturldatabase', companyName, 'items', sanitizedDocId);
        const urlDocSnap = await getDoc(urlDocRef);
        return urlDocSnap.exists() ? urlDocSnap.data()?.value ?? null : null;
    } catch (error) {
        console.error(`Error fetching client URL for ${docId}:`, error);
        return null;
    }
};

const LatestGpoResultsDashboard: React.FC<LatestGpoResultsDashboardProps> = ({ product }) => {
  const [data, setData] = useState<LatestGpoResultsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { selectedCompany } = usePMT3Selection();
  const [marginHistoryUrl, setMarginHistoryUrl] = useState<string | null>(null);

  // Combined useEffect for fetching GPO data and URL
  useEffect(() => {
    const fetchDataAndUrl = async () => {
      setLoading(true);
      setData(null);
      setError(null);
      setMarginHistoryUrl(null); // Reset URL state

      // Prepare promises
      const promisesToRun: Promise<any>[] = [];

      if (product?.companyName && product?.id) {
        promisesToRun.push(pmt2Service.getLatestGpoResultsData(product.companyName, product.id)); // GPO Data Promise
      } else {
        promisesToRun.push(Promise.resolve(null)); // Placeholder if no product
      }

      if (selectedCompany) {
        promisesToRun.push(fetchClientUrl(selectedCompany, 'Margin History')); // URL Promise
      } else {
        promisesToRun.push(Promise.resolve(null)); // Placeholder if no company
      }

      try {
        const results = await Promise.allSettled(promisesToRun);

        // Process GPO Data Result
        if (results[0].status === 'fulfilled') {
          setData(results[0].value); // Can be null if no product
        } else {
          console.error('Error fetching GPO data:', results[0].reason);
          setError('Failed to load GPO data.');
          setData(null);
        }

        // Process URL Result
        if (results[1].status === 'fulfilled') {
          setMarginHistoryUrl(results[1].value); // Can be null if no company
        } else {
          console.error('Error fetching Margin History URL:', results[1].reason);
          // Optionally set an error state for the URL fetch, but maybe not critical
          setMarginHistoryUrl(null);
        }

      } catch (err) {
          // This catch block might be redundant with Promise.allSettled
          console.error('Unexpected error during data fetching:', err);
          setError('An unexpected error occurred.');
      } finally {
        setLoading(false); // Set loading false after all fetches are settled
      }
    };

    fetchDataAndUrl();
    // Dependencies: product (for GPO data) and selectedCompany (for URL)
  }, [product, selectedCompany]);


  // Format currency value
  const formatCurrency = (value: number | null | undefined): string => {
    if (value === undefined || value === null) return '-';
    return `$${value.toFixed(2)}`;
  };

  // Format percentage value (standard)
  const formatPercentage = (value: number | null | undefined): string => {
    if (value === undefined || value === null) return '-';
    // Standard formatting multiplies by 100
    return `${(value * 100).toFixed(0)}%`;
  };

  // Format whole dollar currency
  const formatWholeDollar = (value: number | null | undefined): string => {
    if (value === undefined || value === null) return '-';
    const num = Math.round(value); // Round to nearest whole number
    // Add sign for negative numbers, format with commas
    return `${num < 0 ? '-' : ''}$${Math.abs(num).toLocaleString('en-US')}`;
  };

  // Get conditional classes based on value
  // Get conditional text color classes based on value (positive/negative)
  const getConditionalClasses = (value: number | null | undefined): string => {
    if (value === undefined || value === null) return '';
    return value < 0 ? 'text-red-600 dark:text-red-400' :
           value > 0 ? 'text-green-700 dark:text-green-400' : ''; // Green for positive, red for negative
  };

  // Get conditional classes for GPOs Per Month
  // Get conditional text color classes for GPOs Per Month
  const getGposPerMonthClasses = (value: number | null | undefined): string => {
    if (value === undefined || value === null) return '';
    return value < 1.0 ? 'text-red-600 dark:text-red-400' : // Red if less than 1.0
           'text-green-700 dark:text-green-400'; // Green if 1.0 or more
  };

  // Use Card component for consistent styling
  const renderContent = () => {
     if (loading) {
      return <Skeleton className="h-[200px] w-full" />;
    }
    if (error) {
      return <p className="text-red-600 text-sm p-4">Error: {error}</p>;
    }
    // Show message if no product is selected OR if data is null after loading
    if (!product || !data) {
      return <p className="text-gray-500 dark:text-gray-400 p-4">Select a product to view data.</p>;
    }

    // Render data once loaded, no errors, and data exists
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* First column */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/2">Item</TableHead>
                <TableHead className="w-1/2 text-right">Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium py-1 px-2">Current Actual Gross Profit</TableCell> {/* Reduced padding */}
                <TableCell className="text-right py-1 px-2">{formatCurrency(data.currentActualGrossProfit)}</TableCell> {/* Reduced padding */}
              </TableRow>
              <TableRow>
                <TableCell className="font-medium py-1 px-2">Current GPO</TableCell> {/* Reduced padding */}
                <TableCell className="text-right py-1 px-2">{formatCurrency(data.currentGpo)}</TableCell> {/* Reduced padding */}
              </TableRow>
              <TableRow>
                <TableCell className="font-medium py-1 px-2">Previous GPO</TableCell> {/* Reduced padding */}
                <TableCell className="text-right py-1 px-2">{formatCurrency(data.previousGpo)}</TableCell> {/* Reduced padding */}
              </TableRow>
              <TableRow>
                <TableCell className="font-medium py-1 px-2">Days Passed</TableCell> {/* Reduced padding */}
                <TableCell className="text-right py-1 px-2">{data.daysPassed ?? '-'}</TableCell> {/* Reduced padding */}
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Second column */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/2">Item</TableHead>
                <TableHead className="w-1/2 text-right">Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium py-1 px-2">Gross Profit Before</TableCell> {/* Reduced padding */}
                <TableCell className="text-right py-1 px-2">{formatWholeDollar(data.grossProfitBefore)}</TableCell> {/* Use new format */}
              </TableRow>
              <TableRow>
                <TableCell className="font-medium py-1 px-2">Gross Profit After</TableCell> {/* Reduced padding */}
                <TableCell className={cn("text-right py-1 px-2", getConditionalClasses(data.grossProfitAfter))}> {/* Reduced padding & text color */}
                   <div className="flex items-center justify-end space-x-1">
                        {data.grossProfitAfter !== null && data.grossProfitAfter !== undefined && data.grossProfitAfter < 0 && <AlertCircle size={16} />}
                        {data.grossProfitAfter !== null && data.grossProfitAfter !== undefined && data.grossProfitAfter > 0 && <CheckCircle size={16} />}
                        <span>{formatWholeDollar(data.grossProfitAfter)}</span> {/* Use new format */}
                   </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium py-1 px-2">Change $</TableCell> {/* Reduced padding */}
                <TableCell className={cn("text-right py-1 px-2", getConditionalClasses(data.changeAmount))}> {/* Reduced padding & text color */}
                   <div className="flex items-center justify-end space-x-1">
                        {data.changeAmount !== null && data.changeAmount !== undefined && data.changeAmount < 0 && <AlertCircle size={16} />}
                        {data.changeAmount !== null && data.changeAmount !== undefined && data.changeAmount > 0 && <CheckCircle size={16} />}
                        <span>{formatWholeDollar(data.changeAmount)}</span> {/* Use new format */}
                   </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium py-1 px-2">Change %</TableCell> {/* Reduced padding */}
                <TableCell className={cn("text-right py-1 px-2", getConditionalClasses(data.changePercent))}> {/* Reduced padding & text color */}
                   <div className="flex items-center justify-end space-x-1">
                        {data.changePercent !== null && data.changePercent !== undefined && data.changePercent < 0 && <AlertCircle size={16} />}
                        {data.changePercent !== null && data.changePercent !== undefined && data.changePercent > 0 && <CheckCircle size={16} />}
                        {/* Format Change % without multiplying by 100 */}
                        <span>{data.changePercent !== null && data.changePercent !== undefined ? `${data.changePercent.toFixed(0)}%` : '-'}</span>
                   </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium py-1 px-2">Total GPOs</TableCell> {/* Reduced padding */}
                <TableCell className="text-right py-1 px-2">{data.totalGpos ?? '-'}</TableCell> {/* Reduced padding */}
              </TableRow>
              <TableRow>
                <TableCell className="font-medium py-1 px-2">GPOs Per Month</TableCell> {/* Reduced padding */}
                <TableCell className={cn("text-right py-1 px-2", getGposPerMonthClasses(data.gposPerMonth))}> {/* Reduced padding & text color */}
                   <div className="flex items-center justify-end space-x-1">
                        {data.gposPerMonth !== null && data.gposPerMonth !== undefined && data.gposPerMonth < 1.0 && <AlertCircle size={16} />}
                        {data.gposPerMonth !== null && data.gposPerMonth !== undefined && data.gposPerMonth >= 1.0 && <CheckCircle size={16} />}
                        <span>{data.gposPerMonth?.toFixed(1) ?? '-'}</span>
                   </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  return (
    <Card className="max-w-2xl mx-auto"> {/* Reduced width */}
        <CardHeader className="p-4">
            <div className="flex items-center gap-2">
                <CardTitle className="text-2xl font-bold">Latest GPO Results</CardTitle>
                {marginHistoryUrl && (
                     <a
                        href={marginHistoryUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Margin History Sheet"
                        className="text-blue-500 hover:text-blue-700 inline-flex items-center justify-center"
                     >
                        <LinkIcon size={18} />
                     </a>
                )}
            </div>
        </CardHeader>
        <CardContent className="p-4">
            {renderContent()}
        </CardContent>
    </Card>
  );
};

export default LatestGpoResultsDashboard;